import {
  Container,
  ContainerContent,
  ContainerHeader,
} from '@clearcover/atlas';
import {
  PolicyActivity,
  PolicyActivityBase,
  PolicyRolledBackActivity,
  PolicyUpdatedActivity,
} from '@clearcover/core-app-types';
import {
  mapWithDefault,
  mapWithDefaultProvider,
  usePolicyContext,
} from 'Policies/utils';
import { CardSectionInfoPairs } from 'Shared/components';
import { InfoValuePairDataType } from 'Shared/components/Card/CardSectionInfoPairs/InfoPair';
import { snakeToTitleCase } from 'Shared/utils';
import React, { FC } from 'react';
import { formatSummaryElement } from '../Common';
import commonStyles from '../Common/styles.module.css';
import { KnownActivityType, KnownTypes } from '../utils';
import { PolicyChanges } from './PolicyChanges';
import styles from './styles.module.css';

type PolicyActivityComponent = FC<{ activity: PolicyActivityBase }>;

const Titles = mapWithDefaultProvider<KnownActivityType, string>({
  defaultProvider: snakeToTitleCase,
  map: {
    POLICY_UPDATED: 'Policy Change',
    POLICY_ROLLBACK: 'Rolled Back',
  },
});

const PolicyRollbackChanges: FC<{ activity: PolicyActivityBase }> = ({
  activity,
}) => {
  const rollbackActivity = activity as PolicyRolledBackActivity;
  if (!rollbackActivity.data.changeset) {
    return <></>;
  }

  // Map rollback-specific fields to the shape PolicyChanges/PolicyUpdatedActivity expects.
  // PolicyChanges reads activity.data.versionId to fetch the version diff data —
  // POLICY_ROLLBACK's data has baseVersionId (the rollback target) instead, so we
  // map it through here. Without this mapping PolicyChangeVehicleDetails fires
  // requests to /policies/${policyId}/version/undefined.
  const syntheticActivity = {
    ...rollbackActivity,
    data: {
      ...rollbackActivity.data,
      versionId: rollbackActivity.data.baseVersionId,
      changeset: rollbackActivity.data.changeset,
    },
  } as unknown as PolicyUpdatedActivity;

  return <PolicyChanges activity={syntheticActivity} />;
};

const BottomComponents = mapWithDefault<
  keyof typeof KnownTypes,
  FC<{ activity: PolicyActivityBase }>
>({
  defaultValue: () => <></>,
  map: {
    POLICY_UPDATED: PolicyChanges as PolicyActivityComponent,
    POLICY_ROLLBACK: PolicyRollbackChanges,
  },
});

const Headers = {
  TRANSACTION_DATE: 'Transaction Date',
  EFFECTIVE_DATE: 'Effective Date',
  RATED_PREMIUM_CHANGE: 'Rated Premium / Change',
  INITIATED_BY: 'Initiated By',
};
type DataField = [string, any];
type DataFieldProvider = (
  activity: PolicyActivityBase,
  timeZone?: string
) => Array<DataField>;
const initiatedByFieldProvider: DataFieldProvider = (activity) => [
  [Headers.INITIATED_BY, activity.createdBy],
];

const AdditionalActivityFields = mapWithDefault<
  KnownActivityType,
  DataFieldProvider
>({
  defaultValue: (_activity, _timezone) => [],
  map: {
    POLICY_UPDATED: initiatedByFieldProvider as DataFieldProvider,
    POLICY_ROLLBACK: initiatedByFieldProvider,
  },
});

const ActivityInfo: FC<{ activity: PolicyActivityBase }> = ({ activity }) => {
  const timeZone = usePolicyContext().details.timeZone;
  const basicFields: [string, InfoValuePairDataType][] =
    activity.summary.generalData.map((summaryElement) => [
      summaryElement.label,
      formatSummaryElement(summaryElement, timeZone),
    ]);
  const additionalFields = AdditionalActivityFields.get(activity.type)(
    activity,
    timeZone
  );
  const fields = [...basicFields, ...additionalFields];
  return (
    <CardSectionInfoPairs
      className={commonStyles.activityInfoGrid}
      headers={fields.map(([header]) => header)}
      data={fields.map(([, value]) => value)}
    />
  );
};

export const PolicyActivityDetails: FC<{
  activity: PolicyActivity;
}> = ({ activity }) => {
  const { type } = activity;
  const title = Titles.get(type);
  const BottomComponent = BottomComponents.get(type);
  return (
    <Container style={{ alignItems: 'center' }}>
      <ContainerHeader className={styles.title}>{title}</ContainerHeader>
      <ContainerContent className={styles.content}>
        <ActivityInfo activity={activity} />
        <BottomComponent activity={activity} />
      </ContainerContent>
    </Container>
  );
};
